import { useTranslate } from '@providers/TranslateProvider';

import { cx } from '@libs/classnames';
import { formatPossibleAnonymousName } from '@libs/formatNames';
import { AnimatedNode } from '@ui/components/Animation';
import { BoardColorCircle } from '@ui/components/BoardColorCircle';
import { GameScore } from '@ui/components/GameScore/GameScore';

import { TableTimer } from '@features/game/ui/TableTimer';
import { get2x2TeamPlayerNumber } from '@features/game/utils/get2x2TeamPlayerNumber';

import { calculateClock } from './calculateClock';
import { IPanelGame, IScoresheetClocks } from './types';

import styles from './PlayerPanelAlt.module.css';

/*
    ____,-------------------------------,____
    \   |          Компонент            |   /
    /___|-------------------------------|___\
*/

export type Props = {
  position?: 'top' | 'bottom';
  color: 'black' | 'white';
  game: IPanelGame;
  ratingGain?: number;
  //-
  customPlayersTimeMs?: IScoresheetClocks;
  timePressureSec: number;
  fractionsFromSec?: number;
  isFirstMoveTime?: boolean;
  //-
  networkProblem?: boolean;
};

export function PlayerPanelAltDesktop({
  position = 'top',
  color,
  game,
  ratingGain,
  //
  customPlayersTimeMs,
  timePressureSec = 10,
  fractionsFromSec = 10,
  isFirstMoveTime,
  //
  networkProblem,
}: Props) {
  const isWhite = color === 'white';
  const player = game[color];
  const name = formatPossibleAnonymousName(player);

  // Для игры 2на2 (по два игрока за один цвет)
  const player2 = game[`${color}SecondPlayer`];
  const name2 = player2 ? formatPossibleAnonymousName(player2) : null;
  const activePlayerNumber = player2 ? get2x2TeamPlayerNumber(game.moves.length) : null;

  const isDraw = game.result === 'Draw';
  const isWin = isWhite ? game.result === 'WhiteWin' : game.result === 'BlackWin';
  const isDefeat = !isWhite ? game.result === 'WhiteWin' : game.result === 'BlackWin';

  const { t } = useTranslate();

  const { leftMs, customTimeMs, paused } = calculateClock(
    game,
    color,
    customPlayersTimeMs,
    isFirstMoveTime,
  );
  const timestampMs = game.clock?.timestampMs;

  const hasNoTimeControl = leftMs === undefined;

  const nodeResult = (
    <AnimatedNode
      className={cx(styles.result, {
        [styles.section_counter__result]: !hasNoTimeControl,
      })}
    >
      {isDraw && <GameScore className={styles.section_player__score} highlightDraw score={0.5} />}
      {isWin && <GameScore className={styles.section_player__score} score={1} />}
      {isDefeat && <GameScore className={styles.section_player__score} score={0} />}
    </AnimatedNode>
  );

  return (
    <div
      className={cx(styles.wrapper, {
        [styles.is_network]: networkProblem,
        [styles.is_win]: isWin,
        [styles.is_top]: position === 'top',
        [styles.is_bottom]: position === 'bottom',
        [styles.has_no_time]: hasNoTimeControl,
      })}
    >
      <section className={styles.player_panel}>
        <div
          className={cx(styles.section_result, {
            [styles.is_first_move_time_active]: !paused && isFirstMoveTime,
          })}
        >
          <AnimatedNode>
            {isWin && t('@features/game/pages/GamePage/DrawOffer/PlayerPanelAlt/Win')}
            {!isWin && isFirstMoveTime && !paused && <>на первый ход</>}
          </AnimatedNode>
        </div>
        {!hasNoTimeControl && timestampMs && (
          <div className={styles.section_counter}>
            <div
              className={cx(styles.section_counter__counter, {
                [styles.is_first_move_time]: !paused && isFirstMoveTime,
              })}
            >
              <TableTimer
                className={styles.section_counter__counter__timer}
                preset="big"
                leftMs={customTimeMs || leftMs}
                timestampMs={timestampMs}
                paused={paused}
                timePressureSec={timePressureSec}
                fractionsFromSec={fractionsFromSec}
                timeSync
              />
            </div>
            <div className={styles.section_counter__rating_gain}>
              <AnimatedNode>{formatDelta(ratingGain)}</AnimatedNode>
            </div>
            {nodeResult}
            <AnimatedNode className={styles.network_problem}>
              {networkProblem && game.result === 'InProgress' && 'Проблемы с сетью'}
            </AnimatedNode>
          </div>
        )}
        <section className={styles.players_slot}>
          <div
            className={cx(styles.section_player, {
              [styles.is_highlighted]: !paused && activePlayerNumber === 1,
            })}
          >
            <div className={styles.section_player__name}>
              <BoardColorCircle className={styles.section_player__color} color={color} />
              {name}
            </div>
            <div className={styles.section_player__rating}>
              {player.rating}{' '}
              <AnimatedNode tag="span">{formatDelta(player.ratingChange)}</AnimatedNode>
            </div>
          </div>
          {player2 && (
            <div
              className={cx(styles.section_player, {
                [styles.is_highlighted]: !paused && activePlayerNumber === 2,
              })}
            >
              <div className={styles.section_player__name}>
                <BoardColorCircle className={styles.section_player__color} color={color} />
                {name2}
              </div>
              <div className={styles.section_player__rating}>
                {player2.rating}{' '}
                <AnimatedNode tag="span">{formatDelta(player2.ratingChange)}</AnimatedNode>
              </div>
            </div>
          )}
        </section>
      </section>
      {hasNoTimeControl && nodeResult}
    </div>
  );
}

/*
    ____,-------------------------------,____
    \   |            Утилиты            |   /
    /___|-------------------------------|___\
*/

function formatDelta(d?: number | null): string | null {
  if (!d) return null;
  return d < 0 ? d.toString() : `+${d}`;
}
